"use client";

/**
 * Client-side Clerk shim.
 *
 * If NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY is set, re-exports the real Clerk
 * components and hooks. If the env var is missing (e.g. local training-machine
 * dev setup with no secrets), exports no-op stubs so the app still renders
 * with the "signed out" state instead of crashing.
 *
 * Use this everywhere instead of importing from "@clerk/nextjs" directly.
 */

import type { ReactNode } from "react";
import * as Clerk from "@clerk/nextjs";

const HAS_CLERK =
    typeof process !== "undefined" &&
    !!process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY;

const PassThrough = ({ children }: { children?: ReactNode }) => <>{children}</>;
const Empty = () => null;

/* ── Visibility components ───────────────────────────────── */
// SignedOut content shows when no Clerk is configured (treat user as guest).
// SignedIn content stays hidden.
export const SignedIn = (HAS_CLERK ? Clerk.SignedIn : Empty) as typeof Clerk.SignedIn;
export const SignedOut = (HAS_CLERK ? Clerk.SignedOut : PassThrough) as typeof Clerk.SignedOut;
export const ClerkLoaded = (HAS_CLERK ? Clerk.ClerkLoaded : PassThrough) as typeof Clerk.ClerkLoaded;
export const ClerkLoading = (HAS_CLERK ? Clerk.ClerkLoading : Empty) as typeof Clerk.ClerkLoading;

/* ── Provider ────────────────────────────────────────────── */
export const ClerkProvider = (HAS_CLERK ? Clerk.ClerkProvider : PassThrough) as typeof Clerk.ClerkProvider;

/* ── Action buttons ──────────────────────────────────────── */
// SignInButton with no Clerk: render its children (typically a styled button)
// so the layout still looks right; clicking does nothing.
export const SignInButton = (HAS_CLERK ? Clerk.SignInButton : PassThrough) as typeof Clerk.SignInButton;
export const SignUpButton = (HAS_CLERK ? Clerk.SignUpButton : PassThrough) as typeof Clerk.SignUpButton;

const stubUserButton: any = () => null;
stubUserButton.MenuItems = PassThrough;
stubUserButton.Link = () => null;
stubUserButton.Action = () => null;
stubUserButton.UserProfilePage = () => null;
stubUserButton.UserProfileLink = () => null;
export const UserButton = (HAS_CLERK ? Clerk.UserButton : stubUserButton) as typeof Clerk.UserButton;

/* ── Hooks ───────────────────────────────────────────────── */
const stubUseAuth = () => ({
    isLoaded: true,
    isSignedIn: false,
    userId: null,
    sessionId: null,
    actor: null,
    orgId: null,
    orgRole: null,
    orgSlug: null,
    has: () => false,
    signOut: async () => {},
    getToken: async () => null,
});

const stubUseUser = () => ({
    isLoaded: true,
    isSignedIn: false,
    user: null,
});

export const useAuth = (HAS_CLERK ? Clerk.useAuth : stubUseAuth) as typeof Clerk.useAuth;
export const useUser = (HAS_CLERK ? Clerk.useUser : stubUseUser) as typeof Clerk.useUser;

/* ── Convenience flag for callers that want to branch UX ── */
export const CLERK_ENABLED = HAS_CLERK;
